import { ReactNode } from "react";
import { m as motion, useReducedMotion } from "framer-motion";

/**
 * Vector scribble accents for inline copy. SVG strokes stay crisp at any
 * size, and draw themselves on when scrolled into view (skipped for users
 * who prefer reduced motion).
 */

const draw = (delay: number, reduce: boolean | null) =>
  reduce
    ? {}
    : {
        initial: { pathLength: 0, opacity: 0 },
        whileInView: { pathLength: 1, opacity: 1 },
        viewport: { once: true, amount: 0.9 },
        transition: { duration: 0.7, delay, ease: "easeInOut" as const },
      };

export function CircleWord({
  children,
  delay = 0.3,
  color = "#d82435",
}: {
  children: ReactNode;
  delay?: number;
  color?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <span className="relative inline-block">
      {children}
      <svg
        viewBox="0 0 120 52"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 !w-[132%] !h-[158%] pointer-events-none"
      >
        {/* hand-drawn double-pass ellipse */}
        <motion.path
          d="M113 20.5 C108 8.5 84 3.5 59 4.5 C31 5.6 6.5 13 6 26 C5.5 39 30 48.5 61 47.5 C90 46.6 114 38.5 114.5 25.5 C115 15.5 97 7.5 76 6"
          stroke={color}
          strokeWidth="3.2"
          strokeLinecap="round"
          {...draw(delay, reduce)}
        />
      </svg>
    </span>
  );
}

export function ScribbleUnder({
  children,
  delay = 0.3,
  color = "#1A1A1A",
  thick = false,
}: {
  children: ReactNode;
  delay?: number;
  color?: string;
  thick?: boolean;
}) {
  const reduce = useReducedMotion();
  return (
    <span className="relative inline-block whitespace-nowrap">
      {children}
      <svg
        viewBox="0 0 200 14"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
        className={`absolute left-1/2 -translate-x-1/2 !w-[103%] pointer-events-none ${thick ? "bottom-[-20px] !h-[16px]" : "bottom-[-9px] !h-[10px]"}`}
      >
        {/* two rough underline strokes */}
        <motion.path
          d="M3 5 C55 2.5 140 2 197 4"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          {...draw(delay, reduce)}
        />
        <motion.path
          d="M14 10.5 C70 8.5 130 8 186 9.5"
          stroke={color}
          strokeWidth="2.2"
          strokeLinecap="round"
          {...draw(delay + 0.25, reduce)}
        />
      </svg>
    </span>
  );
}
